// @ts-strict-ignore
import { component } from "picoapp";
import { createCardPlaceholders, stringToHTML } from "../../utilities";

const scrollIntoViewWithOffset = (node: Element, offset: number) => {
  window.scrollTo({
    behavior: "smooth",
    top:
      node.getBoundingClientRect().top -
      document.body.getBoundingClientRect().top -
      offset,
  });
};

const parseDom = (stringToMarkup: string) => {
  const dom = stringToHTML(stringToMarkup);
  const grid = dom.querySelector("[data-tbsp-component$='Grid']");
  const items = grid.querySelectorAll("* > main > ol > li");
  const pageNextUrl = grid.querySelector<HTMLDivElement>("[data-page-next-url]")
    ?.dataset?.pageNextUrl;
  const pageNextSize = parseFloat(
    grid.querySelector<HTMLDivElement>("[data-page-next-size]")?.dataset
      ?.pageNextSize ?? "0"
  );

  const paginationCounter: HTMLParagraphElement = grid.querySelector(
    "#load-more-pagination p"
  );

  const paginationPercentage: HTMLDivElement = grid.querySelector(
    "#pagination-percentage"
  );

  return {
    dom,
    grid,
    items,
    pageNextUrl,
    pageNextSize,
    paginationCounter,
    paginationPercentage,
  };
};

const parseNextUrl = (nextPath: string, sectionId: string) => {
  const nextUrl = new URL(`${window.location.origin}${nextPath}`);
  nextUrl.searchParams.append("sections", sectionId);

  return nextUrl;
};

export const gridPagination = component((node: HTMLButtonElement) => {
  const pageOnScroll = parseFloat(node.dataset.pageOnscroll ?? "0");
  let currentPageOnScroll = 0;
  const sectionGrid = node.closest("[data-tbsp-component$='Grid']");
  const section = sectionGrid.closest("[id^='shopify-section']");
  const pagination: HTMLDivElement = sectionGrid.querySelector(
    "#load-more-pagination"
  );

  if (!pagination) {
    return;
  }

  pagination.classList.replace("hidden", "flex");

  const loadMoreButton = pagination.querySelector("button");
  const paginationPercentage: HTMLDivElement = pagination.querySelector(
    "#pagination-percentage"
  );
  const [, sectionId] = section.getAttribute("id").split("shopify-section-");

  if (!loadMoreButton) {
    return;
  }

  const loadMoreHandler = async (e: Event) => {
    e.preventDefault();

    loadMoreButton.setAttribute("disabled", "true");
    const pageNextSize = parseFloat(node.dataset.pageNextSize ?? "0");
    const pageNextUrl = node.dataset.pageNextUrl;
    const placeholders = createCardPlaceholders(pageNextSize, "lg");
    sectionGrid.querySelector("* > main > ol").append(...placeholders);

    if (!pageNextUrl) {
      return;
    }

    let partialMarkup: string;

    try {
      const result = await fetch(parseNextUrl(pageNextUrl, sectionId)).then(
        (res) => res.json()
      );
      partialMarkup = result[sectionId];
    } catch (e) {
      console.error(e);
    }

    if (!partialMarkup) {
      return;
    }

    const xDom = parseDom(partialMarkup);

    placeholders.forEach((ph, idx) => {
      if (!xDom.items[idx]) {
        ph.parentElement.removeChild(ph);
      } else {
        ph.replaceWith(xDom.items[idx]);
      }
    });

    node.setAttribute("data-page-next-url", xDom.pageNextUrl);
    node.setAttribute("data-page-next-size", String(xDom.pageNextSize));

    window.dispatchEvent(
      new Event("shopify:section:load", {
        bubbles: true,
        cancelable: true,
      })
    );

    setTimeout(() => {
      scrollIntoViewWithOffset(xDom.items[0], 100);
    }, 250);

    const paginationCounter: HTMLParagraphElement =
      pagination.querySelector("p");

    if (currentPageOnScroll === pageOnScroll) {
      node.classList.replace("opacity-0", "opacity-100");
      node.classList.replace("translate-y-full", "translate-y-0");
    }

    if (!xDom.paginationCounter) {
      paginationCounter.textContent = `${pagination.dataset.totalItems} / ${pagination.dataset.totalItems}`;
      paginationPercentage.style.width = "100%";

      setTimeout(() => {
        node.classList.replace("opacity-100", "opacity-0");
        node.classList.replace("translate-y-0", "translate-y-full");
      }, 500);
    } else {
      paginationCounter.replaceWith(xDom.paginationCounter);
      paginationPercentage.style.width = xDom.paginationPercentage.style.width;
      loadMoreButton.removeAttribute("disabled");
    }
  };

  if (pageOnScroll > 0) {
    node.classList.add(...["opacity-0", "translate-y-full"]);
  }

  loadMoreButton.addEventListener("click", loadMoreHandler);

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          currentPageOnScroll = currentPageOnScroll + 1;

          // Fire a syntetic load more event
          loadMoreButton.click();

          if (currentPageOnScroll === pageOnScroll) {
            observer.unobserve(node);
          }
        }
      });
    },
    { rootMargin: "0px" }
  );

  if (pageOnScroll > 0) {
    observer.observe(node);
  }

  return (node: HTMLButtonElement) => {
    node.removeEventListener("click", loadMoreHandler);
  };
});
